import React, {Component} from 'react';
import Alert from '@material-ui/lab/Alert';

/**
 * Mockup screen S8's "What refusal looks like" (docs/mockups/access-control-mockups.html; slice
 * 4b-2 §3.9).
 *
 * Three messages, and what they have in common is that each says **what to do next** rather than
 * 403. A refusal a user cannot act on is a support ticket with extra steps.
 *
 * KEYED ON THE WIRE CODES, NOT THE INTERNAL ONES. `AccessWriteError.code` is `R1`/`R2` inside the
 * model, and `controllers/access/assignments.js:30` renders those as `ESCALATION_BLOCKED_R1` /
 * `ESCALATION_BLOCKED_R2` on the way out — a component mapping `R1` would never match the one
 * surface that emits refusals. A plan revision asserted the opposite, having checked
 * `ACCESS_WRITE_STATUS` (the code → HTTP-status map) instead of the wire, and a test written to
 * agree with it would have passed.
 */

const MESSAGES = {
  ESCALATION_BLOCKED_R2: (err) => ({
    title: 'You can\'t give yourself this.',
    // The server's message already names the (permission, scope) pair R2 refused — the most
    // specific fact available, and re-deriving it here would only let the two drift.
    body: `${err.error} Ask another administrator to make the change.`,
  }),
  ESCALATION_BLOCKED_R1: (err) => ({
    title: 'You can\'t grant what you don\'t hold.',
    body: `${err.error} Someone who already holds it has to grant it.`,
  }),
  GRANT_CONFERS_NOTHING: (err) => ({
    title: 'This would grant nothing.',
    body: `${err.error} Pick a scope on each tree the role covers, or choose a role that covers only one.`,
  }),
  DUPLICATE_GRANT: () => ({
    title: 'They already have this.',
    body: 'This subject already holds this grant at this scope. Nothing to do.',
  }),
  NO_ACTOR_SUBJECT: () => ({
    title: 'Your account has no access subject.',
    body: 'Every grant and revoke will be refused until one is provisioned. Run ' +
      'scripts/access/check-grant-coverage.js.',
  }),
  ACCESS_WRITE_BUSY: () => ({
    title: 'Another administrator is making a change.',
    body: 'Access writes are serialised so two admins cannot widen each other. Retry in a moment.',
  }),
};

class AccessRefusal extends Component {
  render() {
    const {error} = this.props;
    if (!error) return null;

    const build = MESSAGES[error.code];
    // NO CODE AT ALL is a real case, not a defensive branch: the three `routes/admin.js` membership
    // sites send an HTTP status from `accessWriteStatus` but no `code` field, so a group-membership
    // refusal arrives as prose. Rendering the server's own message beats rendering nothing.
    const {title, body} = build ? build(error) : {title: null, body: error.error};

    return (
      <Alert severity="warning" className="access-refusal" data-testid="ac-refusal">
        {title && <strong className="refusal-title">{title}</strong>}
        <span className="refusal-body">{body}</span>
        {this.props.children}
      </Alert>
    );
  }
}

export default AccessRefusal;
